import React from 'react';
import Card from '../../ui/Card';
import DescriptionPanel from '../../ui/DescriptionPanel';
import { TrendingUpIcon, TrendingDownIcon } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
const AftermarketSalesDashboard: React.FC = () => {
  const kpis = [{
    title: 'Total Aftermarket Revenue',
    value: '₹2,960 Cr',
    change: '+8.5%',
    isPositive: true
  }, {
    title: 'Avg Channel Margin',
    value: '22.4%',
    change: '+1.2%',
    isPositive: true
  }, {
    title: 'Active Dealers',
    value: '534',
    change: '+28',
    isPositive: true
  }, {
    title: 'Online Channel %',
    value: '18.5%',
    change: '+3.2%',
    isPositive: true
  }, {
    title: 'Return Rate',
    value: '2.8%',
    change: '-0.4%',
    isPositive: true
  }];
  const channelData = [{
    channel: 'Distributor',
    revenue: 1480,
    margin: 18.5,
    growth: 7.2
  }, {
    channel: 'Retailer',
    revenue: 1185,
    margin: 24.2,
    growth: 9.5
  }, {
    channel: 'Online',
    revenue: 295,
    margin: 28.5,
    growth: 22.8
  }];
  const topSKUsData = [{
    sku: 'LED Headlamp Assembly',
    revenue: 285,
    units: 125000,
    margin: 26.5
  }, {
    sku: 'Tail Lamp Complete',
    revenue: 245,
    units: 185000,
    margin: 24.2
  }, {
    sku: 'Side Mirror (L+R)',
    revenue: 198,
    units: 95000,
    margin: 22.8
  }, {
    sku: 'Door Handle Set',
    revenue: 165,
    units: 142000,
    margin: 21.5
  }, {
    sku: 'Bumper Guard Kit',
    revenue: 142,
    units: 68000,
    margin: 28.2
  }, {
    sku: 'Fog Lamp Assembly',
    revenue: 128,
    units: 88000,
    margin: 25.8
  }];
  const regionSKUData = [{
    region: 'North',
    lighting: 95,
    polymer: 88,
    electrical: 92,
    safety: 78
  }, {
    region: 'West',
    lighting: 92,
    polymer: 85,
    electrical: 90,
    safety: 82
  }, {
    region: 'South',
    lighting: 88,
    polymer: 92,
    electrical: 85,
    safety: 75
  }, {
    region: 'East',
    lighting: 82,
    polymer: 78,
    electrical: 80,
    safety: 70
  }];
  const dealerWalletData = [{
    dealer: 'Premium Auto Parts',
    ourShare: 42,
    competitors: 58,
    potential: 185
  }, {
    dealer: 'Speed Motors',
    ourShare: 38,
    competitors: 62,
    potential: 165
  }, {
    dealer: 'City Spares Hub',
    ourShare: 52,
    competitors: 48,
    potential: 142
  }, {
    dealer: 'Metro Auto Zone',
    ourShare: 35,
    competitors: 65,
    potential: 198
  }, {
    dealer: 'Express Parts',
    ourShare: 48,
    competitors: 52,
    potential: 128
  }];
  const returnAnalysisData = [{
    reason: 'Quality Issues',
    count: 285,
    percentage: 32,
    value: 42.5
  }, {
    reason: 'Wrong Part Shipped',
    count: 195,
    percentage: 22,
    value: 28.8
  }, {
    reason: 'Fitment Issues',
    count: 165,
    percentage: 18,
    value: 24.2
  }, {
    reason: 'Customer Changed Mind',
    count: 142,
    percentage: 16,
    value: 18.5
  }, {
    reason: 'Damaged in Transit',
    count: 108,
    percentage: 12,
    value: 15.8
  }];
  const COLORS = ['#ef4444', '#f59e0b', '#3b82f6', '#10b981', '#8b5cf6'];
  return <div data-id="element-8180">
      <DescriptionPanel title="Aftermarket Sales Performance" description="Comprehensive analysis of aftermarket channels, SKU performance, and dealer network effectiveness. Track online growth, wallet share opportunities, and quality issues." insights={['Online channel growing 22.8% YoY - now 18.5% of aftermarket revenue', 'LED headlamp assemblies leading SKU at ₹285 Cr with 26.5% margin', 'Top 5 dealers represent 42% potential wallet share opportunity', 'Quality-related returns down to 2.8% from 3.2% - continuous improvement working']} data-id="element-8181" />
      {/* KPIs */}
      <div className="mb-8" data-id="element-8182">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6" data-id="element-8183">
          {kpis.map((kpi, index) => <Card key={index} className="p-5 bg-gradient-to-br from-white to-gray-50" data-id="element-8184">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide" data-id="element-8185">
                {kpi.title}
              </div>
              <div className="mt-3 text-3xl font-bold text-gray-900" data-id="element-8186">
                {kpi.value}
              </div>
              <div className={`mt-2 text-sm flex items-center font-semibold ${kpi.isPositive ? 'text-green-600' : 'text-red-600'}`} data-id="element-8187">
                {kpi.isPositive ? <TrendingUpIcon size={16} className="mr-1" data-id="element-8188" /> : <TrendingDownIcon size={16} className="mr-1" data-id="element-8189" />}
                {kpi.change}
              </div>
            </Card>)}
        </div>
      </div>
      {/* Channel Performance */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8" data-id="element-8190">
        <Card className="p-6" data-id="element-8191">
          <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-8192">
            Channel Performance (₹ Cr)
          </h3>
          <ResponsiveContainer width="100%" height={300} data-id="element-8193">
            <BarChart data={channelData} data-id="element-8194">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-8195" />
              <XAxis dataKey="channel" tick={{
              fontSize: 12
            }} data-id="element-8196" />
              <YAxis tick={{
              fontSize: 12
            }} data-id="element-8197" />
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} data-id="element-8198" />
              <Legend data-id="element-8199" />
              <Bar dataKey="revenue" fill="#3b82f6" name="Revenue (₹ Cr)" data-id="element-8200" />
              <Bar dataKey="margin" fill="#10b981" name="Margin %" data-id="element-8201" />
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card className="p-6" data-id="element-8202">
          <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-8203">
            Channel Growth Rate (%)
          </h3>
          <ResponsiveContainer width="100%" height={300} data-id="element-8204">
            <BarChart data={channelData} data-id="element-8205">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-8206" />
              <XAxis dataKey="channel" tick={{
              fontSize: 12
            }} data-id="element-8207" />
              <YAxis tick={{
              fontSize: 12
            }} data-id="element-8208" />
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} data-id="element-8209" />
              <Bar dataKey="growth" fill="#10b981" name="YoY Growth %" data-id="element-8210" />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>
      {/* Top SKUs */}
      <Card className="p-6 mb-8" data-id="element-8211">
        <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-8212">
          Top Aftermarket SKUs
        </h3>
        <div className="overflow-x-auto" data-id="element-8213">
          <table className="w-full" data-id="element-8214">
            <thead data-id="element-8215">
              <tr className="border-b border-gray-200" data-id="element-8216">
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700" data-id="element-8217">
                  SKU
                </th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700" data-id="element-8218">
                  Revenue
                </th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700" data-id="element-8219">
                  Units Sold
                </th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700" data-id="element-8220">
                  Margin %
                </th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700" data-id="element-8221">
                  Contribution
                </th>
              </tr>
            </thead>
            <tbody data-id="element-8222">
              {topSKUsData.map((item, index) => <tr key={index} className="border-b border-gray-100 hover:bg-gray-50" data-id="element-8223">
                  <td className="py-3 px-4 text-sm font-medium text-gray-800" data-id="element-8224">
                    {item.sku}
                  </td>
                  <td className="py-3 px-4 text-sm text-right text-gray-700" data-id="element-8225">
                    ₹{item.revenue} Cr
                  </td>
                  <td className="py-3 px-4 text-sm text-right text-gray-700" data-id="element-8226">
                    {item.units.toLocaleString()}
                  </td>
                  <td className="py-3 px-4 text-sm text-right font-medium text-green-600" data-id="element-8227">
                    {item.margin}%
                  </td>
                  <td className="py-3 px-4 text-right" data-id="element-8228">
                    <div className="flex items-center justify-end" data-id="element-8229">
                      <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden mr-2" data-id="element-8230">
                        <div className="h-full bg-blue-500" style={{
                      width: `${item.revenue / 285 * 100}%`
                    }} data-id="element-8231" />
                      </div>
                      <span className="text-xs font-semibold text-gray-700" data-id="element-8232">
                        {(item.revenue / 1163 * 100).toFixed(1)}%
                      </span>
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </Card>
      {/* Region vs SKU Heatmap */}
      <Card className="p-6 mb-8" data-id="element-8233">
        <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-8234">
          Region vs Product Category Performance Heatmap
        </h3>
        <div className="overflow-x-auto" data-id="element-8235">
          <table className="w-full" data-id="element-8236">
            <thead data-id="element-8237">
              <tr className="border-b border-gray-200" data-id="element-8238">
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700" data-id="element-8239">
                  Region
                </th>
                <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700" data-id="element-8240">
                  Lighting
                </th>
                <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700" data-id="element-8241">
                  Polymer
                </th>
                <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700" data-id="element-8242">
                  Electrical
                </th>
                <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700" data-id="element-8243">
                  Safety
                </th>
              </tr>
            </thead>
            <tbody data-id="element-8244">
              {regionSKUData.map((item, index) => <tr key={index} className="border-b border-gray-100" data-id="element-8245">
                  <td className="py-3 px-4 text-sm font-medium text-gray-800" data-id="element-8246">
                    {item.region}
                  </td>
                  {['lighting', 'polymer', 'electrical', 'safety'].map(category => {
                const value = item[category];
                const bgColor = value >= 90 ? 'bg-green-500' : value >= 85 ? 'bg-green-400' : value >= 80 ? 'bg-yellow-400' : 'bg-orange-400';
                return <td key={category} className="py-3 px-4 text-center" data-id="element-8247">
                          <div className={`${bgColor} text-white font-semibold text-sm py-2 px-3 rounded inline-block`} data-id="element-8248">
                            {value}
                          </div>
                        </td>;
              })}
                </tr>)}
            </tbody>
          </table>
        </div>
        <div className="mt-4 flex items-center justify-end space-x-2 text-xs" data-id="element-8249">
          <span className="text-gray-600" data-id="element-8250">Performance Index:</span>
          <div className="flex items-center space-x-1" data-id="element-8251">
            <div className="w-4 h-4 bg-green-500 rounded" data-id="element-8252" />
            <span className="text-gray-600" data-id="element-8253">≥90</span>
          </div>
          <div className="flex items-center space-x-1" data-id="element-8254">
            <div className="w-4 h-4 bg-yellow-400 rounded" data-id="element-8255" />
            <span className="text-gray-600" data-id="element-8256">80-89</span>
          </div>
          <div className="flex items-center space-x-1" data-id="element-8257">
            <div className="w-4 h-4 bg-orange-400 rounded" data-id="element-8258" />
            <span className="text-gray-600" data-id="element-8259">&lt;80</span>
          </div>
        </div>
      </Card>
      {/* Dealer Wallet Share */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8" data-id="element-8260">
        <Card className="p-6" data-id="element-8261">
          <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-8262">
            Dealer Wallet Share Analysis
          </h3>
          <div className="space-y-4" data-id="element-8263">
            {dealerWalletData.map((dealer, index) => <div key={index} className="border-b border-gray-100 pb-3 last:border-0" data-id="element-8264">
                <div className="flex items-center justify-between mb-2" data-id="element-8265">
                  <span className="text-sm font-medium text-gray-800" data-id="element-8266">
                    {dealer.dealer}
                  </span>
                  <span className="text-xs text-gray-600" data-id="element-8267">
                    Potential: ₹{dealer.potential} Cr
                  </span>
                </div>
                <div className="flex items-center" data-id="element-8268">
                  <div className="flex-1 h-6 bg-gray-200 rounded-lg overflow-hidden flex" data-id="element-8269">
                    <div className="bg-blue-500 flex items-center justify-center text-white text-xs font-semibold" style={{
                  width: `${dealer.ourShare}%`
                }} data-id="element-8270">
                      {dealer.ourShare}%
                    </div>
                    <div className="bg-red-300 flex items-center justify-center text-gray-700 text-xs font-semibold" style={{
                  width: `${dealer.competitors}%`
                }} data-id="element-8271">
                      {dealer.competitors}%
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between mt-1 text-xs text-gray-600" data-id="element-8272">
                  <span data-id="element-8273">Our Share</span>
                  <span data-id="element-8274">Competitors</span>
                </div>
              </div>)}
          </div>
        </Card>
        <Card className="p-6" data-id="element-8275">
          <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-8276">
            Sales Return/Defect Analysis
          </h3>
          <ResponsiveContainer width="100%" height={250} data-id="element-8277">
            <PieChart data-id="element-8278">
              <Pie data={returnAnalysisData} cx="50%" cy="50%" labelLine={false} label={({
              reason,
              percentage
            }) => `${reason}: ${percentage}%`} outerRadius={80} fill="#8884d8" dataKey="percentage" data-id="element-8279">
                {returnAnalysisData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} data-id="element-8280" />)}
              </Pie>
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} data-id="element-8281" />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-4 space-y-2" data-id="element-8282">
            {returnAnalysisData.map((item, index) => <div key={index} className="flex items-center justify-between text-xs" data-id="element-8283">
                <div className="flex items-center" data-id="element-8284">
                  <div className="w-3 h-3 rounded mr-2" style={{
                backgroundColor: COLORS[index]
              }} data-id="element-8285" />
                  <span className="text-gray-700" data-id="element-8286">{item.reason}</span>
                </div>
                <span className="font-semibold text-gray-800" data-id="element-8287">
                  ₹{item.value} Lakhs
                </span>
              </div>)}
          </div>
        </Card>
      </div>
    </div>;
};
export default AftermarketSalesDashboard;